import React, { FC, useCallback, useEffect, useState } from "react";
import { IncClickAwayProps } from "./types";

const IncClickAway: FC<IncClickAwayProps> = props => {
  const { children, onClickAway } = props;

  const [docEl, setDocEl] = useState<HTMLElement | null>(null);
  const ref = useCallback((element: HTMLElement | null) => setDocEl(element), []);

  useEffect(() => {
    if (!docEl) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Skip if clicking select options
      if (target?.classList.contains("inception-react-select__option")) {
        return;
      }

      // Check if click is inside the current popper element
      if (docEl.contains(target)) {
        return;
      }

      // Walk up the DOM tree to check for other poppers
      let currentElement: HTMLElement | null = target;
      let foundPopper: HTMLElement | null = null;

      while (currentElement && currentElement !== document.body) {
        if (currentElement.classList.contains("inc-popper")) {
          foundPopper = currentElement;
          break;
        }
        currentElement = currentElement.parentElement;
      }

      // If we found another popper, check the relationship
      if (foundPopper && foundPopper !== docEl) {
        // Check if the found popper is a child of the current popper
        // (nested inside the current popper's DOM)
        if (docEl.contains(foundPopper)) {
          // This is a child popper nested inside the current popper
          // Don't close when clicking inside a child popper
          return;
        }

        // Check if the current popper is nested inside the found popper
        // (current popper is a child of the found popper)
        if (foundPopper.contains(docEl)) {
          onClickAway();
          return;
        }

        // Check DOM order as fallback (later in DOM typically means rendered later = inner)
        const currentPosition = Array.from(document.querySelectorAll(".inc-popper")).indexOf(docEl);
        const foundPosition = Array.from(document.querySelectorAll(".inc-popper")).indexOf(foundPopper);

        // If found popper appears later in DOM, it's the "inner" popper
        // Don't close current (outer) popper when clicking on inner
        if (foundPosition > currentPosition) {
          return;
        }

        onClickAway();
        return;
      }

      onClickAway();
    };

    // Use mousedown - fires before potential stopPropagation on click
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [docEl, onClickAway]);

  return <>{children(ref)}</>;
};

export default IncClickAway;
